import { isEmpty } from 'lodash/fp';
import { useState } from 'react';

import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import { CaseDataManager } from 'dashboard/models/case';
import { ModuleInputSelect } from 'dashboard/modules/InputSelect/model';
import Card from 'design-system/components/Card';
import { Label, Paragraph } from 'design-system/components/Text';
import { FieldSchema, FieldSchemaType } from 'lib/fields';

export interface InputSelectProps {
  module: ModuleInputSelect;
  fieldSchema: FieldSchema[];
  caseDataManager: CaseDataManager;
  isPreview: boolean;
  updateCaseField: (fieldsToUpdate: Record<string, any>) => void;
}

const InputSelect = ({
  module,
  fieldSchema,
  caseDataManager,
  isPreview = false,
  updateCaseField,
}: InputSelectProps) => {
  const [fieldValue, setFieldValue] = useState<string>(
    caseDataManager.case ? caseDataManager.case.fields[module['field-key']] : '',
  );

  const handleOnBlur: () => void = () => {
    if (isPreview) {
      return null;
    } else {
      // Select option values must be text, and so we need to convert the empty string
      // value of the "nothing selected" option to be null when it gets sent to the server.
      const serverFieldValue = fieldValue === '' ? null : fieldValue;
      updateCaseField({ [module['field-key']]: serverFieldValue });
    }
  };

  const selectedFieldSchema = fieldSchema.find(
    (schema) => schema.key === module['field-key'].split('-').join('_'),
  );

  if (
    selectedFieldSchema &&
    selectedFieldSchema.type !== FieldSchemaType.Choices &&
    selectedFieldSchema.type !== FieldSchemaType.MultiChoices
  ) {
    return (
      <Card data-test={`module__input--${module.token}`}>
        <Card.Body>
          <Label id='input-select-label' size='md' weight='bold'>
            {module.label}
          </Label>
          <Paragraph>
            Your module is incorrectly configured. Please check your configurations and reload the
            page.
          </Paragraph>
        </Card.Body>
      </Card>
    );
  }

  let options: OptionType[];
  // selectedFieldSchema can be null in Case Template Editor when a field-key is not yet selected.
  if (selectedFieldSchema) {
    const schemaOptions: string[] =
      selectedFieldSchema.type === FieldSchemaType.Choices ||
      selectedFieldSchema.type === FieldSchemaType.MultiChoices
        ? (selectedFieldSchema.config?.options ?? [])
        : [];

    const optionLabels: string[] =
      selectedFieldSchema.type === FieldSchemaType.Choices ||
      selectedFieldSchema.type === FieldSchemaType.MultiChoices
        ? (selectedFieldSchema.config?.['option-labels'] ?? [])
        : [];

    const allowEmpty: boolean =
      selectedFieldSchema.type === FieldSchemaType.Choices ||
      selectedFieldSchema.type === FieldSchemaType.MultiChoices
        ? (selectedFieldSchema.config?.['allow-empty'] ?? false)
        : false;

    const configOptions: { label: string; value: string }[] = (schemaOptions ?? []).map(
      (option, idx) => {
        const label =
          optionLabels && optionLabels.length !== 0 && optionLabels?.[idx]
            ? optionLabels[idx]
            : option;
        return {
          label: label,
          value: option,
        };
      },
    );
    options = allowEmpty ? [{ label: 'Select...', value: '' }, ...configOptions] : configOptions;
  } else {
    options = [];
  }

  return (
    <Card data-test={`module__input--${module.token}`}>
      <Card.Body>
        <Label id='input-select-label' size='md' weight='bold'>
          {module.label}
        </Label>
        <ReactSelect
          aria-labelledby='input-select-label'
          id={`input-select--${module.token}`}
          placeholder='Select...'
          onChange={setFieldValue}
          onBlur={handleOnBlur}
          disabled={isEmpty(module['field-key'])}
          value={fieldValue}
          options={options}
          portal
          isMulti={selectedFieldSchema && selectedFieldSchema.type === FieldSchemaType.MultiChoices}
        />
      </Card.Body>
    </Card>
  );
};

export default InputSelect;
